import { useCallback } from 'react'
import Button from '@mui/material/Button'
import DialogCloseButton from '@/components/DialogCloseButton'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { Group, PartialClientOrganization, createClientOrganization } from '@/api'
import useTranslate from '@/hooks/use-translate'
import { useFormik } from 'formik'
import * as Yup from 'yup'

export type CreateCustomerModalProps = {
  open: boolean
  onClose: () => void
  onCreated: () => void
  groups: Group[]
  organizationId?: string
}

export const CreateCustomerModal = ({
  open,
  onClose,
  onCreated,
  groups,
  organizationId,
}: CreateCustomerModalProps) => {
  const { t } = useTranslate('customerFlow')
  const { t: tCommon } = useTranslate('common')
  const { t: tManagementConsole } = useTranslate('managementConsole')

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('customerNameRequired')),
    groupId: Yup.string().required(t('agencyRequired')),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      groupId: groups[0]?.gid ?? '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload: PartialClientOrganization = {
        name: values.name,
        group: values.groupId ? [values.groupId] : [],
        organization: organizationId,
      }
      await createClientOrganization(payload)
      formik.resetForm()
      onCreated()
      onClose()
    },
  })

  const handleClose = useCallback(() => {
    formik.resetForm()
    onClose()
  }, [formik, onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
      }}
    >
      <DialogTitle>{t('addCustomer')}</DialogTitle>
      <DialogCloseButton onClose={handleClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label={tManagementConsole('clientOrganizationName')}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <FormControl fullWidth error={formik.touched.groupId && Boolean(formik.errors.groupId)}>
            <InputLabel id="create-customer-agency-label">
              {t('columnAgency')}
            </InputLabel>
            <Select
              labelId="create-customer-agency-label"
              label={t('columnAgency')}
              name="groupId"
              value={formik.values.groupId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {groups.map((group) => (
                <MenuItem key={group.gid} value={group.gid}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{tCommon('cancel')}</Button>
        <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
          {tCommon('create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
